/**
 * Every tunable in one place.
 *
 * These are game-design parameters, not measurements. The target is behaviour that is
 * predictable, expressive and slightly exaggerated — not physically correct. Units are
 * world units (wu) and seconds; the world is a fixed WORLD_WIDTH x WORLD_HEIGHT box that
 * the renderer letterboxes into whatever viewport it gets, so physics is identical on
 * every device.
 *
 * The camera looks straight down at a flat table. Gravity therefore points *into* the
 * screen and does nothing in-plane: nothing moves unless a magnet moves it, which is what
 * makes this a puzzle rather than a race against falling. What gravity leaves behind is
 * friction — the fluid and the ball are both resting on a surface, so both bleed speed.
 */

/** Logical playfield. All simulation state lives in this space; CSS pixels are UI-only. */
export const WORLD_WIDTH = 1000;
export const WORLD_HEIGHT = 1600;

/** Simulation runs at a fixed rate, decoupled from the display refresh rate. */
export const FIXED_STEP = 1 / 60;
/** Never simulate more than this much wall-clock time in one frame (spiral-of-death guard). */
export const MAX_FRAME_TIME = 0.25;

/** Hard ceiling on simultaneous magnets — also the shader's uniform array size. */
export const MAX_MAGNETS = 8;

export const PHYSICS = {
  // --- magnetic field ----------------------------------------------------
  /** Overall scale of the magnetic force. Bigger = stronger pull at every distance. */
  magneticStrength: 1.9e6,
  /**
   * Force falls off as 1 / distance^falloff.
   *
   * Not 2, deliberately. An inverse-square field has a range ratio of a hundred to one
   * across this board, which means whichever magnet is nearest wins absolutely: a magnet
   * placed anywhere along a route dominates the source as well, so it is impossible to
   * nudge a stream sideways without also stopping it leaving. Flattening the falloff
   * brings that ratio down to roughly twenty-five to one, and steering becomes possible.
   * Predictable and expressive beats correct here.
   */
  magneticFalloff: 1.35,
  /** Distances below this are clamped, so standing on a magnet does not explode. */
  magneticMinDistance: 70,
  /** Ceiling on magnetic acceleration applied to one fluid particle (wu/s^2). */
  magneticMaxForce: 12000,

  // --- world -------------------------------------------------------------
  /**
   * In-plane gravity. Zero for the top-down view: the table is level, so a blob sits
   * where it is put. Give it a value to tilt the table.
   */
  gravity: 0,

  // --- ferrofluid --------------------------------------------------------
  /**
   * Collision radius of a single fluid particle against walls and the ball.
   *
   * Its ratio to particleRestDistance is a packing fraction, and that is the number that
   * decides whether this behaves like a liquid or like gravel: pi*r^2 / (0.866*d^2). At
   * 6 : 12.5 it is 0.84, which is 2D random close packing — the blob jams and translates
   * instead of flowing. Lower the radius or raise the spacing to loosen it.
   */
  particleRadius: 6,
  /**
   * Separation the relaxation solver drives neighbours towards.
   *
   * Also the Vogel-spiral spacing in World.fill(), so changing it silently resizes the
   * starting blob and can truncate how many particles fit in a chamber.
   */
  particleRestDistance: 12.5,
  /** Neighbour search radius; also the spatial hash cell size. */
  particleInteractionRadius: 26,
  /** Gentle pull between nearby particles — this is what keeps the blob a blob. */
  cohesion: 5200,
  /** Velocity equalisation with neighbours: the "thick oil" knob. */
  viscosity: 0.2,
  /**
   * Velocity retained per second. With no gravity this is the table's friction, and the
   * only thing that brings a blob to rest — so it decides both how far fluid can travel
   * on a given field and how quickly a run settles enough to be judged.
   */
  fluidDamping: 0.3,
  /** Relaxation passes for the non-overlap constraint. More = stiffer, costlier. */
  relaxIterations: 2,
  /**
   * How hard the separation constraint pushes, per iteration.
   *
   * Above 1 is successive over-relaxation, which is valid because solveSeparation writes
   * positions in place — it is a genuine Gauss-Seidel sweep, not Jacobi. Under-relaxing
   * left the fluid measurably over-compressed under field pressure and kept stretched
   * blobs looking like angular debris instead of rounding back up.
   */
  relaxStiffness: 1.15,
  /**
   * Speed clamp, in wu/s. This is the tunnelling guard: at two sub-steps a particle
   * covers speed/120 world units between collision tests, which has to stay below a
   * wall's half thickness plus the particle radius.
   */
  fluidMaxSpeed: 1800,
  /** Energy kept when a particle hits a wall. */
  fluidWallRestitution: 0.1,
  /**
   * Tangential velocity kept per *second* while sliding along a wall.
   *
   * Per-second, not per-step: as a per-step figure this was 0.94 applied 120 times a
   * second, which retains 0.06% and glues fluid to the first wall it touches. Routing a
   * blob around obstacles depends entirely on it being able to slide.
   */
  fluidWallFriction: 0.55,
  /** Physics sub-steps per fixed tick for the fluid only. */
  fluidSubSteps: 2,

  // --- ball --------------------------------------------------------------
  ballRestitution: 0.32,
  ballFriction: 0.9,
  /** Rolling friction against the table. Without gravity this is what parks the ball. */
  ballDrag: 0.85,
  /** How hard a penetrating fluid particle shoves the ball. */
  fluidBallForce: 620,
  /**
   * How strongly the ball is dragged along by the fluid moving around it. This is the
   * dominant term: a stream that flows past the ball has to take it along, or the ball
   * simply parks wherever the first wall stops it.
   */
  fluidBallDrag: 16,
  /** Extra damping applied to the ball while it is buried in fluid. */
  fluidBallDamping: 1.1,

  // --- hooks (not active in this prototype) ------------------------------
  /**
   * Milestone 2+: once the ball is coated it becomes magnetic itself.
   * The coverage metric is computed every tick; only the coupling is switched off.
   */
  ballCoatingEnabled: false,
  ballCoatingThreshold: 0.75,
  ballCoatingStrength: 0.35,
};

export type PhysicsConfig = typeof PHYSICS;

/** Visual-only knobs. Nothing here may feed back into the simulation. */
export const VISUALS = {
  /** Density texture resolution as a fraction of the world box. 0.5 => 500x800. */
  fluidTextureScale: 0.5,
  /** Density texture scale on low-power devices. */
  fluidTextureScaleMobile: 0.42,
  /** Radius of a particle's density splat, in world units. Larger = smoother surface. */
  densitySplatRadius: 28,
  /** Peak contribution of one splat. Tuned so a blob's interior lands near 1.0. */
  densitySplatScale: 0.36,
  /** Iso level at which the density field becomes liquid. */
  densityThreshold: 0.5,
  /** Width of the soft edge around the iso surface. */
  densityEdge: 0.055,
  /**
   * Blur radius in density texels.
   *
   * Has to be comparable to the particle spacing, not smaller: the field is a sum of
   * splats, so below that scale it carries lumps that are an artefact of how the fluid is
   * simulated rather than anything about its surface. Those used to be invisible because
   * the 8-bit density texture saturated the interior flat; on a float texture they show
   * up as mottling across the body. Twelve and a half world units of spacing is six and a
   * quarter texels at the default texture scale.
   */
  densityBlurRadius: 3.5,
  /** How strongly the density gradient bends the surface normal. */
  surfaceNormalStrength: 2.2,
  /**
   * Weight of the *raw* density gradient in the surface normal, relative to the gradient
   * of the thresholded coverage that shapes the rim.
   *
   * This is the whole interior of the blob. It used to be pinned near zero because an
   * 8-bit density texture saturates: a settled blob's interior sums to roughly 1.6 and
   * every one of those texels clamped to 1.0, so the body carried no gradient to read and
   * turning this up only amplified quantisation steps into creases. On a float texture
   * the signal is real, and this is what stops a puddle looking like a decal.
   */
  bodyRelief: 0.40,
  /**
   * Beer-Lambert absorption per unit of density above the surface threshold.
   *
   * Ferrofluid is opaque in bulk and only barely translucent in a thin film, which is
   * exactly the read that makes it look heavy: the rim glows faintly with whatever the
   * room is doing while the middle goes to true black. Without a thickness term every
   * pixel inside the silhouette gets the same ambient and the blob is a flat hole.
   */
  fluidAbsorption: 2.4,

  // --- Rosensweig spikes -------------------------------------------------
  /**
   * Distance between peaks, in world units.
   *
   * In real ferrofluid this is the capillary length, 2*pi*sqrt(surface tension / rho g),
   * which is around a centimetre and — this is the important part — has nothing to do
   * with the magnet. Spacing is a property of the liquid, so it stays constant across the
   * whole crown instead of fanning out with distance.
   */
  spikeWavelength: 36,
  /** How far the rim is dragged out at a full-height peak, in world units. */
  spikeAmplitude: 30,
  /** Cone sharpness. Higher means narrower peaks with flatter ground between them. */
  spikeSharpness: 1.7,
  /** How strongly interior cones tilt the surface normal. This is what makes them visible. */
  spikeRelief: 2.2,
  /** Field magnitude at which the surface goes unstable (wu/s^2). */
  spikeCriticalForce: 2000,
  /**
   * Once a crown has formed it survives a weaker field than it needed to appear — the
   * Rosensweig bifurcation is subcritical. This is the fraction of the critical field an
   * already-spiked region holds on down to.
   */
  spikeHysteresis: 0.6,
  /** Particles that have to be inside a magnet's critical radius before it ignites. */
  spikeIgnitionParticles: 10,

  /** Device pixel ratio ceiling. Phones lie about how many pixels they can push. */
  maxDevicePixelRatio: 2,
  /**
   * Direction the scene is lit from, in world space. Everything that casts a shadow or
   * catches a highlight uses this, so the whole board agrees on where the light is.
   */
  lightDirection: { x: -0.55, y: -0.78 },
  /** Shadow offset in world units, along the light direction. */
  shadowOffset: 13,
  /**
   * Ceiling on how much colour the potential field adds over the table.
   *
   * The field is added, not mixed, so this is a hard cap on how bright the board can get
   * — which is what keeps blacks in the frame for the fluid and the wall shadows to sit
   * against.
   */
  fieldWashGain: 0.16,
  /**
   * Equipotential contours per unit of magnetic potential.
   *
   * High on purpose. A handful of rings is a decoration; dozens of them is an instrument
   * — the lines crowd where the potential is steep, so their density reads directly as
   * how hard the fluid is being pulled.
   */
  fieldContourDensity: 15,

  // --- post-processing ---------------------------------------------------
  /** How much of the blurred bright pass is added back. */
  bloomIntensity: 0.85,
  /** Luminance a pixel needs before it blooms at all. */
  bloomThreshold: 0.52,
  /** Width of the soft knee around that threshold, so highlights fade in rather than pop. */
  bloomKnee: 0.30,
  /** Overall exposure applied before the tonemap. */
  exposure: 1.0,
  /**
   * Subtracted before the tonemap, to put the blacks back after bloom lifts them.
   *
   * Small, because the table is authored at 0.045-0.074 and this comes off the top of it:
   * at 0.012 it was taking a quarter of the ground plane away before the tonemap even ran.
   */
  blackPoint: 0.005,
  /** Contrast applied after the tonemap. */
  contrast: 1.07,
  /**
   * Radial per-channel offset at the frame edge.
   *
   * Small. The board is full of hairline contours, and aberration wide enough to notice
   * on a soft image turns every one of them into a rainbow.
   */
  chromaticAberration: 0.0022,
  /** Corner darkening. */
  vignette: 0.30,
  /** Film grain amplitude. */
  grain: 0.022,
};
